/**
 * Classify client-side API failures so callers can retry or degrade without
 * treating expected operational conditions as application crashes.
 */

function getErrorMessage(error: unknown): string {
  if (typeof error === "string") {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === "object" && "message" in error) {
    const { message } = error as { message: unknown };
    return typeof message === "string" ? message : "";
  }
  return "";
}

/** HTTP 429 or express-rate-limit style messages. */
export function isRateLimitError(error: unknown, status?: number): boolean {
  if (status === 429) {
    return true;
  }
  const message = getErrorMessage(error).toLowerCase();
  return (
    message.includes("429") ||
    message.includes("too many requests") ||
    message.includes("rate limit")
  );
}

/**
 * Navigation abort, Wi‑Fi flap, or interceptor NETWORK_CHANGED — not a product bug.
 * Logging these with console.error floods Sentry via captureConsoleIntegration.
 */
export function isTransientClientFetchError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  if (typeof error === "object" && error !== null) {
    const named = error as { name?: unknown; code?: unknown };
    if (named.name === "AbortError" || named.code === "NETWORK_CHANGED") {
      return true;
    }
  }

  const message = getErrorMessage(error).toLowerCase();
  return (
    message.includes("network connection changed") ||
    message.includes("failed to fetch") ||
    message.includes("err_network_changed") ||
    message.includes("load failed") ||
    message.includes("aborted") ||
    message.includes("the operation was aborted")
  );
}

/**
 * Expected auth/session failures (expired/missing JWT, unauthenticated API calls).
 * These are UX conditions — do not console.error (Sentry captureConsole).
 */
export function isExpectedClientAuthError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  if (typeof error === "object" && error !== null) {
    const { status } = error as { status?: unknown };
    if (status === 401 || status === 403) {
      return true;
    }
  }

  const message = getErrorMessage(error).toLowerCase();
  return (
    message.includes("access token required") ||
    message.includes("invalid token") ||
    message.includes("jwt expired") ||
    message.includes("not authenticated") ||
    message.includes("authorization required") ||
    message.includes("no authorization") ||
    message.includes("unauthorized")
  );
}

export function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Exponential backoff for 429, capped. Honors Retry-After seconds when present. */
export function rateLimitRetryDelayMs(retryCount: number, retryAfterHeader: string | null): number {
  if (retryAfterHeader) {
    const seconds = Number.parseInt(retryAfterHeader, 10);
    if (Number.isFinite(seconds) && seconds > 0) {
      return Math.min(seconds * 1000, 60_000);
    }
  }
  return Math.min(1000 * 2 ** retryCount, 10_000);
}
